import React, { useEffect, useState } from "react";
import {
  UserIcon,
  UserGroupIcon,
  HomeModernIcon,
  UserCircleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from "@heroicons/react/24/outline";
import ProjectsTable from "./ProjectsTable";
import AgentsTable from "./AgentsTable";
import ClientContactsTable from "./ClientContactsTable";
import InputField from "rc-app/components/ClientForm/InputField";
import { CardBorder } from "rc-app/components/CardBorder";
import Icon from "rc-app/components/Icons";
import OperationalDetails from "./OperationalDetails";
import TaxDetails from "./TaxDetails";
import { getClientList } from "rc-app/api/agencyActionsApi";
import {
  getBusinessAdminList,
  getSystemAdminList,
} from "rc-app/api/systemBusinessAdminActionApi";
import UserService from "rc-app/services/UserService";
import PersonFormModal from "rc-app/components/PersonFormModal";
import { useNavigate } from "react-router-dom";
import { getAgentCustomers, getAllAgent } from "rc-app/api/agentActionsApi";
import axios from "axios";
import { toTitleCase } from "rc-app/utils";
import { useDesktopMode } from "rc-app/hooks/useDesktopMode";
import DesktopModePrompt from "rc-app/components/DesktopModePrompt";
// import { AddButton } from "rc-app/components/Buttons";

type TabType =
  | "clientManager"
  | "clientAdmin"
  | "operationalDetails"
  | "taxDetails"
  | "projects"
  | "agents";

// Modal for editing Operational Details
function EditOperationalDetailsModal({
  isOpen,
  onClose,
  onSubmit,
  client,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: any) => void;
  client: any;
}) {
  const [formData, setFormData] = useState({
    incorporation_year: "",
    incorporation_type: "",
    agency_subsidiary_names: "",
    client_acreage_acquired: { value: "", unit: "sqft" },
    client_sqft_built: { value: "", unit: "sqft" },
  });

  useEffect(() => {
    if (isOpen && client) {
      setFormData({
        incorporation_year: client.incorporation_year || "",
        incorporation_type: client.incorporation_type || "",
        agency_subsidiary_names: Array.isArray(client.agency_subsidiary_names)
          ? client.agency_subsidiary_names.join(", ")
          : client.agency_subsidiary_names || "",
        client_acreage_acquired: client.client_acreage_acquired || {
          value: "",
          unit: "sqft",
        },
        client_sqft_built: client.client_sqft_built || {
          value: "",
          unit: "sqft",
        },
      });
    }
  }, [isOpen, client]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="font-inter fixed inset-0 flex flex-col bg-black bg-opacity-80 items-center justify-center p-4 z-50">
      <div className="flex flex-col w-2/3 bg-[#1C2C3A] rounded-xl shadow-2xl overflow-y-auto">
        <div className="pb-6 pt-10 px-6">
          <CardBorder
            title="Operational Details"
            borderColor="border-[#2E4051]"
          >
            <div className="flex w-full md:w-1/2 px-3 mb-6">
              <InputField
                label="Client Incorporation Year"
                name="incorporation_year"
                value={formData.incorporation_year}
                placeholder="Enter 4-digit year"
                maxLength={4}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    incorporation_year: e.target.value,
                  })
                }
              />
            </div>

            <div className="flex w-full md:w-1/2 px-3 mb-6">
              <InputField
                label="Client Incorporation Type"
                name="incorporation_type"
                value={formData.incorporation_type}
                placeholder="Enter incorporation type"
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    incorporation_type: e.target.value,
                  })
                }
              />
            </div>

            <div className="flex w-full md:w-1/2 px-3 mb-6">
              <InputField
                label="Client Subsdiary Names "
                name="agency_subsidiary_names"
                value={formData.agency_subsidiary_names}
                placeholder="Enter subsidiary names"
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    agency_subsidiary_names: e.target.value,
                  })
                }
              />
            </div>

            <div className="flex w-full md:w-1/2 px-3 mb-6">
              <InputField
                label="Client Acreage Completed"
                name="client_acreage_acquired"
                value={formData.client_acreage_acquired.value}
                placeholder="Enter acreage"
                type="number"
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    client_acreage_acquired: {
                      ...formData.client_acreage_acquired,
                      value: e.target.value,
                    },
                  })
                }
              />
            </div>

            <div className="flex w-full md:w-1/2 px-3 mb-6">
              <InputField
                label="Client Sqft Built"
                name="client_sqft_built"
                value={formData.client_sqft_built?.value}
                placeholder="Enter sqft built"
                type="number"
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    client_sqft_built: {
                      ...formData.client_sqft_built,
                      value: e.target.value,
                    },
                  })
                }
              />
            </div>

          </CardBorder>

          <div className="flex justify-center gap-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-[#E7EEF9] rounded-lg bg-[#2E4051] hover:opacity-90"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-[#008564] text-[#F4F5F7] rounded-lg hover:opacity-90"
              onClick={handleSubmit}
            >
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Modal for editing Tax Details
function EditTaxDetailsModal({
  isOpen,
  onClose,
  onSubmit,
  client,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: any) => void;
  client: any;
}) {
  const [formData, setFormData] = useState({
    gstn: "",
    pan: "",
    invoice_collected_to_date: { value: 0, currency_code: "INR" },
    tax_collected_to_date: { value: 0, currency_code: "INR", tax_rate: 0 },
    tax_paid_to_date: { value: 0, currency_code: "INR", tax_rate: 0 },
  });

  const [errors, setErrors] = useState({
    gstn: "",
    pan: "",
  });

  useEffect(() => {
    if (isOpen && client) {
      setFormData({
        gstn: client.gstn || "",
        pan: client.pan || "",
        invoice_collected_to_date: client.invoice_collected_to_date || {
          value: 0,
          currency_code: "INR",
        },
        tax_collected_to_date: client.tax_collected_to_date || {
          value: 0,
          currency_code: "INR",
          tax_rate: 0,
        },
        tax_paid_to_date: client.tax_paid_to_date || {
          value: 0,
          currency_code: "INR",
          tax_rate: 0,
        },
      });
    }
  }, [isOpen, client]);

  const validateField = (field: string, value: string) => {
    switch (field) {
      case "gstn":
        // Allow empty, but validate format if provided
        if (value.trim() === "") return "";
        if (
          !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(
            value,
          )
        ) {
          return "Invalid GSTN format (e.g., 29ABCDE1234F1Z5)";
        }
        return "";
      case "pan":
        // Allow empty, but validate format if provided
        if (value.trim() === "") return "";
        if (!/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(value)) {
          return "Invalid PAN format (e.g., AEAPZ5569J)";
        }
        return "";
      default:
        return "";
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const newErrors = {
      gstn: validateField("gstn", formData.gstn),
      pan: validateField("pan", formData.pan),
    };

    setErrors(newErrors);

    if (Object.values(newErrors).some((error) => error !== "")) {
      return;
    }

    onSubmit(formData);
    onClose();
  };

  const handleFieldChange = (field: string, value: string) => {
    setFormData({ ...formData, [field]: value });
    if (errors[field as keyof typeof errors]) {
      setErrors({ ...errors, [field]: validateField(field, value) });
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex flex-col bg-black bg-opacity-80 items-center justify-center p-4 z-50">
      <div className="flex flex-col w-2/3 bg-[#1C2C3A] rounded-xl shadow-2xl overflow-y-auto">
        <div className="p-6">
          <CardBorder title="Tax Details" borderColor="border-[#2E4051]">
            <div className="flex w-full md:w-1/2 px-3 mb-6">
              <InputField
                label="Client GSTIN"
                name="gstn"
                value={formData.gstn}
                placeholder="Enter GSTIN (e.g., 29ABCDE1234F1Z5)"
                maxLength={15}
                onChange={(e) =>
                  handleFieldChange("gstn", e.target.value.toUpperCase())
                }
                error={errors.gstn}
              />
            </div>

            <div className="flex w-full md:w-1/2 px-3 mb-6">
              <InputField
                label="Client PAN"
                name="pan"
                value={formData.pan}
                placeholder="Enter PAN (e.g., AEAPZ5569J)"
                maxLength={10}
                onChange={(e) =>
                  handleFieldChange("pan", e.target.value.toUpperCase())
                }
                error={errors.pan}
              />
            </div>

            <div className="flex w-full md:w-1/2 px-3 mb-6">
              <InputField
                label="Invoice collected to date in INR"
                name="invoice_collected"
                value={formData.invoice_collected_to_date.value}
                placeholder="Enter amount"
                type="number"
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    invoice_collected_to_date: {
                      ...formData.invoice_collected_to_date,
                      value: parseFloat(e.target.value) || 0,
                    },
                  })
                }
              />
            </div>

            <div className="flex w-full md:w-1/2 px-3 mb-6">
              <InputField
                label="GST Collected in INR"
                name="tax_collected"
                value={formData.tax_collected_to_date.value}
                placeholder="Enter amount"
                type="number"
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    tax_collected_to_date: {
                      ...formData.tax_collected_to_date,
                      value: parseFloat(e.target.value) || 0,
                    },
                  })
                }
              />
            </div>

            <div className="flex w-full md:w-1/2 px-3 mb-6">
              <InputField
                label="GST Paid in INR"
                name="tax_paid"
                value={formData.tax_paid_to_date.value}
                placeholder="Enter amount"
                type="number"
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    tax_paid_to_date: {
                      ...formData.tax_paid_to_date,
                      value: parseFloat(e.target.value) || 0,
                    },
                  })
                }
              />
            </div>
          </CardBorder>

          <div className="flex justify-center gap-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-[#E7EEF9] rounded-lg bg-[#2E4051] hover:opacity-90"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-[#008564] text-[#F4F5F7] rounded-lg hover:opacity-90"
              onClick={handleSubmit}
            >
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function TableSkeleton() {
  return (
    <div className="animate-pulse">
      <div className="bg-[#2E4051] rounded-lg p-4 mb-2">
        <div className="h-4 bg-[#374B5E] rounded w-3/4 mb-2"></div>
        <div className="h-4 bg-[#374B5E] rounded w-1/2"></div>
      </div>
      <div className="bg-[#2E4051] rounded-lg p-4 mb-2">
        <div className="h-4 bg-[#374B5E] rounded w-2/3 mb-2"></div>
        <div className="h-4 bg-[#374B5E] rounded w-1/3"></div>
      </div>
      <div className="bg-[#2E4051] rounded-lg p-4">
        <div className="h-4 bg-[#374B5E] rounded w-3/4 mb-2"></div>
        <div className="h-4 bg-[#374B5E] rounded w-1/2"></div>
      </div>
    </div>
  );
}

function ClientCard({ clientId, client, contact }: any) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabType>("clientManager");
  const [expanded, setExpanded] = useState(false);
  const [showClientManagerModal, setShowClientManagerModal] = useState(false);
  const [showClientAdminModal, setShowClientAdminModal] = useState(false);

  const [editingBA, setEditingBA] = useState<any>(null);
  const [editingSA, setEditingSA] = useState<any>(null);
  const [editOperationalModal, setEditOperationalModal] = useState(false);
  const [editTaxModal, setEditTaxModal] = useState(false);

  const [clientManager, setClientManager] = useState([]);
  const [clientManagerId, setClieetManagerId] = useState();
  const [clientAdmin, setClientAdmin] = useState([]);
  const [agentList, setAgentList] = useState([]);

  const [projects, setProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(false);

  const [loadingManager, setLoadingManager] = useState(false);
  const [loadingAdmin, setLoadingAdmin] = useState(false);

  // Add local client state
  const [localClient, setLocalClient] = useState(client);
  const [logoLoadError, setLogoLoadError] = useState(false);

  // Update localClient when client prop changes
  useEffect(() => {
    setLocalClient(client);
  }, [client]);

  useEffect(() => {
    setLogoLoadError(false);
  }, [localClient?.logo]);

  function fetchBusinessAdminList(clientId: any) {
    setLoadingManager(true);
    getBusinessAdminList(clientId)
      .then((res) => {
        console.log("API Response:", res.agency_business_admin_list);
        setClientManager(res.agency_business_admin_list);
        setClieetManagerId(
          res.agency_business_admin_list[0]?.agency_business_admin_id,
        );
      })
      .finally(() => setLoadingManager(false));
  }

  function fetchSystemAdminList(clientId: any) {
    setLoadingAdmin(true);
    getSystemAdminList(clientId)
      .then((res) => {
        console.log("API Response Admin:", res.agency_system_admin_list);
        setClientAdmin(res.agency_system_admin_list);
      })
      .finally(() => setLoadingAdmin(false));
  }

  useEffect(() => {
    fetchBusinessAdminList(clientId);
    fetchSystemAdminList(clientId);
  }, [clientId]);

  useEffect(() => {
    let cancelled = false;

    const fetchWithTimeout = (url: string, timeoutMs: number) => {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), timeoutMs);
      return fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${UserService.getToken()}`,
          "ngrok-skip-browser-warning": "disabled",
          "Content-Type": "application/json",
        },
        signal: controller.signal,
      })
        .then((res) => res.json())
        .finally(() => clearTimeout(timer));
    };

    const loadProjects = async () => {
      setLoadingProjects(true);
      try {
        const listResult = await fetchWithTimeout(
          `${process.env.REACT_APP_ONEVERSE_REALESTATE_SERVER_URL}/api/v1/realestate/views/projects?agency_id=${clientId}&page=0&limit=20&lightweight=true`,
          60000,
        );
        if (cancelled) return;
        setProjects(listResult.projects || []);
      } catch (err) {
        console.error("Project list failed:", err);
        if (!cancelled) setProjects([]);
      } finally {
        if (!cancelled) setLoadingProjects(false);
      }
    };

    loadProjects();
    return () => {
      cancelled = true;
    };
  }, [clientId]);

  useEffect(() => {
    if (activeTab !== "agents") return;
    getAllAgent(0, 10000, clientId)
      .then(async (res) => {
        const filteredAgents = res?.re_agent_list || [];

        if (filteredAgents.length === 0) {
          setAgentList([]);
          return;
        }

        const agentsWithCustomerCount = await Promise.all(
          filteredAgents.map(async (agent: any) => {
            try {
              const customerRes = await getAgentCustomers(agent.email_id);
              return {
                ...agent,
                customer_count: customerRes.total_re_customers || 0,
              };
            } catch (error) {
              console.error(
                `Error fetching customers for ${agent.email_id}:`,
                error,
              );
              return {
                ...agent,
                customer_count: 0,
              };
            }
          }),
        );

        setAgentList(agentsWithCustomerCount);
      })
      .catch((error) => {
        console.error("Error fetching agents:", error);
        setAgentList([]);
      });
  }, [clientId, activeTab]);

  console.log("projects1 vvv", agentList);

  const tabs = [
    { id: "clientManager", label: "Business Admin", icon: UserIcon },
    { id: "clientAdmin", label: "System Admin", icon: UserCircleIcon },
    { id: "operationalDetails", label: "Operational Details", icon: UserIcon },
    { id: "taxDetails", label: "Tax Details", icon: UserIcon },
    { id: "projects", label: "Projects", icon: HomeModernIcon },
    { id: "agents", label: "Agents", icon: UserGroupIcon },
  ];

  console.log("cccc", localClient, contact);

  const handleEditOperationalDetails = async (data: any) => {
    try {
      const API_URL = `${process.env.REACT_APP_ONEVERSE_REALESTATE_SERVER_URL}/api/v1/realestate/actions/re_agency/${clientId}`;

      const payload = {
        ...data,
        agency_subsidiary_names: data.agency_subsidiary_names
          .split(",")
          .map((name: string) => name.trim())
          .filter((name: string) => name !== ""),
      };

      await axios.put(API_URL, payload, {
        headers: {
          Authorization: `Bearer ${UserService.getToken()}`,
          "ngrok-skip-browser-warning": "disabled",
          "Content-Type": "application/json",
        },
      });

      // Update local state instead of reloading
      setLocalClient((prev: any) => ({
        ...prev,
        ...payload,
      }));
    } catch (error) {
      console.error("Error updating operational details:", error);
    }
  };

  const handleEditTaxDetails = async (data: any) => {
    try {
      const API_URL = `${process.env.REACT_APP_ONEVERSE_REALESTATE_SERVER_URL}/api/v1/realestate/actions/re_agency/${clientId}`;

      await axios.put(API_URL, data, {
        headers: {
          Authorization: `Bearer ${UserService.getToken()}`,
          "ngrok-skip-browser-warning": "disabled",
          "Content-Type": "application/json",
        },
      });

      // Update local state instead of reloading
      setLocalClient((prev: any) => ({
        ...prev,
        ...data,
      }));
    } catch (error) {
      console.error("Error updating tax details:", error);
    }
  };

  return (
    <>
      <div
        key={clientId}
        className="font-inter bg-[#161F28] rounded-lg border border-[#374B5E] overflow-hidden"
      >
        {/* Client Header - Always Visible */}
        <div className="px-3 py-3 md:p-6">
          <div className="flex flex-col md:flex-row items-start justify-between max-lg:gap-6 ">
            <div className="flex items-start gap-3">
              {localClient?.logo && !logoLoadError ? (
                <div className="w-28 h-28 md:w-32 md:h-32 rounded-lg overflow-hidden border-4 border-[#889DB2] shadow-md bg-[#2E4051] p-2 flex items-center justify-center">
                  <img
                    src={localClient?.logo}
                    alt=""
                    className="w-full h-full object-contain"
                    onError={() => setLogoLoadError(true)}
                  />
                </div>
              ) : (
                <div className="w-28 h-28 md:w-32 md:h-32 rounded-lg flex items-center justify-center bg-[#2E4051] text-[#889DB2] text-3xl md:text-4xl font-bold border-4 border-[#889DB2] shadow-md">
                  {`${localClient?.name?.[0]?.toUpperCase()}${localClient?.name?.[1]?.toUpperCase()}` ||
                    ""}
                </div>
              )}

              <div className="flex flex-col justify-between md:h-24">
                <h2 className="text-xl md:text-2xl font-semibold text-[#F4F5F7]">
                  {toTitleCase(localClient?.name, " ")}
                </h2>
                <div className="flex items-center gap-2 mt-1 text-[#F4F5F7]">
                  <p className="text-sm">{localClient.address}</p>
                </div>
                <div className="flex items-center text-sm text-[#F4F5F7]">
                  <Icon name="ic-card" className="w-5 h-5" />
                  <span className="px-2 text-[#F4F5F7] text-sm font-medium">
                    {localClient?.agency_type} | {localClient?.footprint}
                  </span>
                </div>
                <div className="mt-2">
                  <button
                    onClick={() => navigate(`/add_client?edit=${clientId}`, { state: { clientData: localClient } })}
                    className="flex items-center gap-2 p-3 bg-[#008564] text-[#E7EEF9] text-sm rounded-lg hover:opacity-90 transition-colors font-semibold shadow-lg"
                  >
                    <Icon name="ic-edit-img" className="w-4 h-4" />{" "}
                  </button>
                </div>
              </div>
            </div>

            <div className="flex flex-col md:items-end max-md:gap-1">
              <div className="flex items-center gap-2 text-white">
                <Icon
                  name="ic-handshake"
                  className="w-5 h-5 "
                  style={{ filter: "brightness(0) invert(1)" }}
                />
                <span className="font-normal text-[#889DB2] text-xl">
                  Onboarded by
                </span>
              </div>
              <div className="text-base text-[#F4F5F7]">
                <span className="font-medium">
                  {localClient.channel_partner}
                </span>
              </div>
              {/* <button
                onClick={() => navigate(`/add_client?edit=${clientId}`)}
                className="mt-2 px-3 py-2 flex items-center gap-2  text-white text-sm rounded-lg hover:opacity-90 transition-colors"
              >
                <div className="[filter:invert(45%)_sepia(100%)_saturate(1000%)_hue-rotate(120deg)_brightness(0.9)] group-hover:[filter:brightness(0)_invert(1)]">
                  <Icon name="ic-edit" className="w-4 h-4" />
                </div>
                Edit Client
              </button> */}
            </div>
          </div>

          <div className="flex justify-end items-center">
            <button
              onClick={() => setExpanded(!expanded)}
              className="flex items-center gap-1 text-[#008564] hover:opacity-95 text-sm font-medium"
            >
              {expanded ? (
                <>
                  <ChevronUpIcon className="w-4 h-4" />
                  Show Less
                </>
              ) : (
                <>
                  <ChevronDownIcon className="w-4 h-4" />
                  Show Details
                </>
              )}
            </button>
          </div>
        </div>

        {/* Expandable Content */}
        {expanded && (
          <>
            {/* Tabs  */}
            <div className="flex justify-center items-center">
              <div className="flex overflow-x-auto bg-[#2E4051] rounded-lg p-2 ">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id as TabType)}
                    className={`flex items-center gap-2 px-6 py-3 font-medium text-sm whitespace-nowrap rounded-lg ${
                      activeTab === tab.id
                        ? "bg-[#008564] text-[#E7EEF9] "
                        : "text-[#E7EEF9] "
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Tab Content */}
            <div className="p-6">
              {activeTab === "clientManager" && (
                <div>
                  <div className="overflow-x-auto rounded-lg">
                    {loadingManager ? (
                      <TableSkeleton />
                    ) : (
                      <ClientContactsTable
                        contacts={clientManager}
                        onEdit={(row: any) => setEditingBA(row)}
                        columns={[
                          "Business Admin (BA)",
                          "Phone Number (CM)",
                          "Email (CM)",
                          // "Username (CM)",
                        ]}
                      />
                    )}
                  </div>
                  {/* Edit positioned as the 4th column (far right, aligned after Email) */}
                  <div className="flex w-full justify-end gap-6">
                    {clientManager.length == 0 && (
                      <button
                        onClick={() => {
                          setShowClientManagerModal(true);
                        }}
                        className="mt-6 px-4 py-2 border border-[#008564] bg-[#008564] text-[#E7EEF9] rounded hover:opacity-90 transition-colors font-medium"
                      >
                        + Add
                      </button>
                    )}

                  </div>
                </div>
              )}

              {activeTab === "clientAdmin" && (
                <div>
                  <div className="overflow-x-auto rounded-lg">
                    {loadingAdmin ? (
                      <TableSkeleton />
                    ) : (
                      <ClientContactsTable
                        contacts={clientAdmin}
                        onEdit={(row: any) => setEditingSA(row)}
                        columns={[
                          "System Admin (SA)",
                          "Phone Number (CA)",
                          "Email (CA)",
                          // "Username (CA)",
                        ]}
                      />
                    )}
                  </div>
                  {/* Edit positioned as the 4th column (far right, aligned after Email) */}
                  <div className="flex w-full flex-col items-end gap-1">
                    <div className="flex justify-end gap-6">
                      <button
                        onClick={() => {
                          if (!clientManagerId || clientManager.length === 0)
                            return;
                          setShowClientAdminModal(true);
                        }}
                        disabled={!clientManagerId || clientManager.length === 0}
                        className={`mt-6 px-4 py-2 border rounded transition-colors font-medium ${
                          !clientManagerId || clientManager.length === 0
                            ? "border-[#374B5E] bg-[#374B5E] text-[#889DB2] opacity-50 cursor-not-allowed"
                            : "border-[#008564] bg-[#008564] text-[#E7EEF9] hover:opacity-90"
                        }`}
                      >
                        + Add
                      </button>

                    </div>
                    {(!clientManagerId || clientManager.length === 0) && (
                      <p className="text-[#889DB2] text-xs">
                        Add a Business Admin first
                      </p>
                    )}
                  </div>
                </div>
              )}

              {activeTab === "operationalDetails" && (
                <div>
                  <div className="overflow-x-auto rounded-lg">
                    <OperationalDetails client={localClient} />
                  </div>
                  <div className="flex w-full justify-center gap-6">
                    <button
                      onClick={() => setEditOperationalModal(true)}
                      className="mt-6 px-4 py-2 flex items-center justify-center gap-2 border border-[#008564] bg-[#008564] text-[#E7EEF9] rounded hover:opacity-90 transition-colors font-medium"
                    >
                      <Icon name="ic-edit" className="w-5 h-5" />
                      Edit
                    </button>
                  </div>
                </div>
              )}

              {activeTab === "taxDetails" && (
                <div>
                  <div className="overflow-x-auto rounded-lg">
                    <TaxDetails client={localClient} />
                  </div>
                  <div className="flex w-full justify-center gap-6">
                    <button
                      onClick={() => setEditTaxModal(true)}
                      className="mt-6 px-4 py-2 flex items-center justify-center gap-2 border border-[#008564] bg-[#008564] text-[#E7EEF9] rounded hover:opacity-90 transition-colors font-medium"
                    >
                      <Icon name="ic-edit" className="w-5 h-5" />
                      Edit
                    </button>
                  </div>
                </div>
              )}

              {activeTab === "projects" && (
                <div>
                  <div className="overflow-x-auto rounded-lg">
                    <ProjectsTable
                      projects={projects}
                      loading={loadingProjects}
                      onProjectsReordered={(next) => setProjects(next)}
                    />
                  </div>
                  <div className="flex justify-end mt-4">
                    <button
                      type="button"
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigate(`/add_project/${clientId}`); }}
                      className="px-4 py-2 flex items-center gap-2 bg-[#008564] text-white text-sm rounded-lg hover:opacity-90 transition-colors font-normal"
                      style={{ boxShadow: "0 2px 2px 0 rgba(0, 0, 0, 0.25)" }}
                    >
                      <Icon name="ic-plus" className="w-4 h-4" />
                      New Project
                    </button>
                  </div>
                </div>
              )}

              {activeTab === "agents" && (
                <div>
                  <div className="overflow-x-auto">
                    <AgentsTable agents={agentList} />
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Modals */}
      {showClientManagerModal && (
        <PersonFormModal
          role="businessAdmin"
          mode="create"
          agencyId={clientId}
          existing={(clientManager || []).map((a: any) => ({
            email: a.email_id || "",
            phone: a.mobile_number ? `${a.mobile_number}` : "",
          }))}
          onClose={() => setShowClientManagerModal(false)}
          onSaved={() => {
            setShowClientManagerModal(false);
            fetchBusinessAdminList(clientId);
          }}
        />
      )}

      {editingBA && (
        <PersonFormModal
          role="businessAdmin"
          mode="edit"
          agencyId={clientId}
          record={{
            id: editingBA._id,
            name: editingBA.name || "",
            email: editingBA.email_id || "",
            phone: editingBA.mobile_number ? `${editingBA.mobile_number}` : "",
          }}
          onClose={() => setEditingBA(null)}
          onSaved={() => {
            setEditingBA(null);
            fetchBusinessAdminList(clientId);
          }}
        />
      )}

      {showClientAdminModal && (
        <PersonFormModal
          role="systemAdmin"
          mode="create"
          agencyId={clientId}
          abaId={clientManagerId}
          existing={(clientAdmin || []).map((a: any) => ({
            email: a.email_id || "",
            phone: a.mobile_number ? `${a.mobile_number}` : "",
          }))}
          onClose={() => setShowClientAdminModal(false)}
          onSaved={() => {
            setShowClientAdminModal(false);
            fetchSystemAdminList(clientId);
          }}
        />
      )}

      {editingSA && (
        <PersonFormModal
          role="systemAdmin"
          mode="edit"
          agencyId={clientId}
          record={{
            id: editingSA._id,
            name: editingSA.name || "",
            email: editingSA.email_id || "",
            phone: editingSA.mobile_number ? `${editingSA.mobile_number}` : "",
          }}
          onClose={() => setEditingSA(null)}
          onSaved={() => {
            setEditingSA(null);
            fetchSystemAdminList(clientId);
          }}
        />
      )}

      <EditOperationalDetailsModal
        isOpen={editOperationalModal}
        onClose={() => setEditOperationalModal(false)}
        onSubmit={handleEditOperationalDetails}
        client={localClient}
      />

      <EditTaxDetailsModal
        isOpen={editTaxModal}
        onClose={() => setEditTaxModal(false)}
        onSubmit={handleEditTaxDetails}
        client={localClient}
      />
    </>
  );
}

export default function MyClientsPage() {
  const [clients, setClients] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  /* What the server says exists, against what we were handed — so the
     page can admit when it is showing a subset instead of implying the
     rest is not there. */
  const [clientTotal, setClientTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const [contact] = useState([
    {
      name: "Mr. Akash Agarwal",
      phone: "+91 9618259660",
      email: "Akash_ag@nslinfratech.com",
      username: "Akash_agarwal",
      password: "Admin@12345678",
    },
  ]);

  const navigate = useNavigate();
  const { showPrompt, accept, dismiss } = useDesktopMode();

  /* Re-asks the SERVER whenever the search changes, rather than filtering
     the page that happened to load first. The list is newest-first and
     capped at 200; on stage, where testing leaves hundreds of throwaway
     agencies behind, every real one is older than that window. Searching
     locally could only ever find what was already on screen.

     Debounced, because this fires per keystroke otherwise. */
  useEffect(() => {
    let cancelled = false;
    const run = () => {
      setLoading(true);
      getClientList(searchQuery.trim())
        .then((res: any) => {
          if (cancelled) return;
          setClients(res?.re_agency_list || []);
          setClientTotal(Number(res?.total_re_agencies ?? res?.total_re_agency ?? 0) || 0);
        })
        .catch((error) => {
          if (!cancelled) console.error("Error fetching clients:", error);
        })
        .finally(() => { if (!cancelled) setLoading(false); });
    };
    const t = setTimeout(run, searchQuery.trim() ? 300 : 0);
    return () => { cancelled = true; clearTimeout(t); };
  }, [searchQuery]);

  // Filter clients based on search query
  const filteredClients = clients.filter((client: any) => {
    const query = searchQuery.toLowerCase();
    return (
      client?.name?.toLowerCase().includes(query) ||
      client?.agency_type?.toLowerCase().includes(query) ||
      client?.footprint?.toLowerCase().includes(query) ||
      client?.channel_partner?.toLowerCase().includes(query)
    );
  });

  if (showPrompt) {
    return (
      <DesktopModePrompt
        message="This page contains client management dashboards, data tables, and admin tools that are best viewed in desktop mode. Switch to desktop view for the best experience."
        onAccept={accept}
        onGoBack={() => { dismiss(); navigate("/"); }}
      />
    );
  }

  return (
    <div className=" font-inter min-h-screen bg-[#1C2C3A] p-4 md:p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex justify-between items-center mb-2">
          <h1 className="text-2xl font-bold text-[#F4F5F7]">
            My Clients ({loading ? "..." : filteredClients?.length})
          </h1>
          <div className="flex gap-3">
            <div className="relative">
              <input
                type="text"
                placeholder="Search..."
                // placeholder="Search by name, type, footprint, or partner..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="bg-[#2E4051] text-white px-4 py-2 pl-10 rounded-lg shadow-lg text-sm border-none focus:border-[#008564] focus:outline-none focus:ring-2 focus:ring-[#008564] transition-colors"
              />
              <Icon
                name="ic-search"
                className="absolute left-3 top-2.5 w-4 h-4 text-[#F4F5F7]"
              />
            </div>
            <button
              onClick={() => navigate("/add_client")}
              className="flex items-center gap-2 px-4 py-2 bg-[#008564] text-[#E7EEF9] text-sm rounded-lg hover:opacity-90 transition-colors font-semibold shadow-lg"
            >
              Add New Client +
            </button>
          </div>
        </div>
      </div>

      {/* Client Cards Grid */}
      {/* Client Cards Grid */}
      <div className="grid grid-cols-1 gap-6 mb-8">
        {loading ? (
          <>
            <ClientCardSkeleton />
            <ClientCardSkeleton />
            <ClientCardSkeleton />
            {/* <div className="w-16 h-16 border-4 border-[#008564] border-t-transparent rounded-full animate-spin"></div> */}
          </>
        ) : filteredClients.length > 0 ? (
          <>
            {/* The list is capped at 200, newest first. On stage that
                window is mostly throwaway agencies from testing, so a real
                one can sit outside it — and a page that shows 200 of 400
                while saying nothing is how a super admin concludes an
                agency was deleted. Search asks the server, so it reaches
                past this; the line is here to say the cap exists. */}
            {clientTotal > filteredClients.length && (
              <p className="col-span-full text-sm text-[#889DB2] -mt-2 mb-1">
                Showing {filteredClients.length} of {clientTotal}
                {searchQuery ? " matching" : ""} — search by name to find any
                of the rest.
              </p>
            )}
            {filteredClients.map((client: any) => (
            <ClientCard
              key={client._id}
              clientId={client._id}
              client={client}
              contact={contact}
            />
            ))}
          </>
        ) : (
          <div className="text-center py-12">
            <p className="text-[#889DB2] text-lg">
              {searchQuery
                ? "No clients found matching your search"
                : "No clients available"}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function ClientCardSkeleton() {
  return (
    <div className="bg-[#161F28] rounded-lg border border-[#374B5E] overflow-hidden animate-pulse">
      <div className="px-3 py-3 md:p-4">
        <div className="flex flex-col md:flex-row items-start justify-between max-lg:gap-6">
          <div className="flex items-start gap-3">
            {/* Logo skeleton */}
            <div className="w-28 h-28 md:w-32 md:h-32 rounded-lg bg-[#2E4051]"></div>

            {/* Content skeleton */}
            <div className="flex flex-col justify-between md:h-24 gap-2">
              <div className="h-6 bg-[#2E4051] rounded w-48"></div>
              <div className="h-4 bg-[#2E4051] rounded w-64"></div>
              <div className="h-4 bg-[#2E4051] rounded w-40"></div>
              <div className="h-4 bg-[#2E4051] rounded w-10"></div>
            </div>
          </div>

          {/* Right side skeleton */}
          <div className="flex flex-col md:items-end max-md:gap-1">
            <div className="h-4 bg-[#2E4051] rounded w-32 mb-2"></div>
            <div className="h-4 bg-[#2E4051] rounded w-24"></div>
          </div>
        </div>

        {/* Show Details button skeleton */}
        <div className="flex justify-end items-center mt-4">
          <div className="h-4 bg-[#2E4051] rounded w-24"></div>
        </div>
      </div>
    </div>
  );
}
